import { useEffect, useState } from 'react';
import {
  ORDER_STATUS_LABELS, ALLOCATION_STATUS_LABELS, PATIENT_CATEGORY_LABELS,
  BLOOD_GROUPS, RH_FACTORS, formatRupiah, canTransitionOrder,
  HOSPITAL_CANCELLABLE_STATUSES,
  type OrderStatus, type OrderView, type PatientCategory,
  type BloodGroup, type RhFactor, type Paginated, type HospitalDebt,
  type AllocationResult,
  MAX_PAGE_SIZE,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { FileUpload } from '../components/FileUpload';

interface ComponentRow { id: string; code: string; name: string }
interface Named { id: string; code: string; name: string }
interface OrderRow {
  id: string; orderNumber: string; status: OrderStatus; patientCategory: string;
  submittedAt: string; neededBy: string | null; hospitalCode: string; hospitalName: string;
  requestedBags: number; allocatedBags: number; payableTotal: string;
}

const CATEGORIES: PatientCategory[] =
  ['non_jkn', 'jkn_thalassemia', 'jkn_leukemia', 'jkn_hemodialysis'];

const fmtDate = (iso: string | null) => iso
  ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Statuses that read as "needs attention" get the accent treatment. */
const isAlert = (s: OrderStatus) => s === 'submitted' || s === 'revision_requested';

export function Orders() {
  const { can, user } = useAuth();
  const isHospital = Boolean(user?.hospitalId);

  const [rows, setRows] = useState<OrderRow[]>([]);
  const [components, setComponents] = useState<ComponentRow[]>([]);
  const [branches, setBranches] = useState<Named[]>([]);
  const [selected, setSelected] = useState<OrderView | null>(null);
  const [debts, setDebts] = useState<HospitalDebt[]>([]);
  const [statusFilter, setStatusFilter] = useState<'' | OrderStatus>('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    patientCategory: 'non_jkn' as PatientCategory,
    patientReference: '',
    requestLetterUrl: '',
    componentTypeId: '',
    abo: 'O' as BloodGroup,
    rh: 'positive' as RhFactor,
    quantity: '1',
  });

  async function loadList() {
    setLoading(true);
    try {
      const qs = statusFilter ? `?pageSize=100&status=${statusFilter}` : '?pageSize=100';
      setRows((await api.get<Paginated<OrderRow>>(`/orders${qs}`)).items);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat daftar pesanan.');
    } finally {
      setLoading(false);
    }
  }

  async function loadDebts() {
    if (!can('debt:read')) return;
    setDebts(await api.get<HospitalDebt[]>('/orders/debt').catch(() => []));
  }

  useEffect(() => {
    (async () => {
      const [c, b] = await Promise.all([
        api.get<Paginated<ComponentRow>>(`/blood/components?pageSize=${MAX_PAGE_SIZE}`)
          .catch(() => ({ items: [] as ComponentRow[] })),
        api.get<Paginated<Named>>(`/branches?pageSize=${MAX_PAGE_SIZE}`)
          .catch(() => ({ items: [] as Named[] })),
      ]);
      setComponents(c.items);
      setBranches(b.items);
      if (c.items[0]) setForm((f) => ({ ...f, componentTypeId: c.items[0]!.id }));
    })();
  }, []);
  useEffect(() => { void loadList(); void loadDebts(); }, [statusFilter]);

  async function open(id: string) {
    setNotice(null); setWarn(null); setError(null);
    try {
      setSelected(await api.get<OrderView>(`/orders/${id}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal memuat pesanan.');
    }
  }

  async function act<T>(fn: () => Promise<T>, onOk?: (r: T) => void) {
    setError(null); setNotice(null); setWarn(null);
    try {
      const r = await fn();
      onOk?.(r);
      await loadList();
      await loadDebts();
      if (selected) await open(selected.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Terjadi kesalahan.');
    }
  }

  async function submitOrder(e: React.FormEvent) {
    e.preventDefault();
    await act(
      () => api.post<OrderView>('/orders', {
        patientCategory: form.patientCategory,
        patientReference: form.patientReference || null,
        requestLetterUrl: form.requestLetterUrl || null,
        neededBy: null,
        note: null,
        lines: [{
          componentTypeId: form.componentTypeId,
          abo: form.abo,
          rh: form.rh,
          quantity: Number(form.quantity),
        }],
      }),
      (r) => setNotice(`Pesanan ${r.orderNumber} dikirim dan menunggu review PMI.`),
    );
  }

  async function allocate(id: string, confirmSubstitutions: boolean) {
    let substitutionReason: string | null = null;
    if (confirmSubstitutions) {
      substitutionReason = prompt('Alasan substitusi golongan darah (akan tercatat di audit):');
      if (!substitutionReason) return;
    }
    const branchId = branches.length > 1
      ? (prompt('Batasi ke kode cabang tertentu? Kosongkan untuk semua cabang:') || '').trim()
      : '';
    const target = branchId ? branches.find((b) => b.code === branchId)?.id ?? null : null;

    await act(
      () => api.post<AllocationResult>(`/orders/${id}/allocate`, {
        branchId: target, confirmSubstitutions, substitutionReason,
      }),
      (r) => {
        if (r.shortfalls.length > 0) {
          setWarn(`${r.allocated} kantong dialokasikan. ${r.shortfalls.map((s) => s.message).join(' ')}`);
        } else {
          setNotice(`${r.allocated} kantong dialokasikan. Total ${formatRupiah(r.payableTotal)}.`);
        }
      },
    );
  }

  const totalDebt = debts.reduce((n, d) => n + BigInt(d.outstandingAmount), 0n);

  return (
    <div className="stack">
      <div>
        <h2>Pesanan Darah</h2>
        <p className="muted" style={{ margin: '0.3rem 0 0', fontSize: 14 }}>
          {isHospital
            ? 'Harga yang tercantum saat memesan adalah harga yang akan ditagihkan.'
            : 'Alokasi memakai FEFO — kantong yang paling cepat kadaluarsa diambil lebih dulu.'}
        </p>
      </div>

      {error && <div className="msg error">{error}</div>}
      {notice && <div className="msg ok">{notice}</div>}
      {warn && <div className="msg error" style={{ borderWidth: 2 }}>{warn}</div>}

      {can('debt:read') && debts.length > 0 && (
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <span className="label">{isHospital ? 'Hutang Anda ke PMI' : 'Total hutang rumah sakit'}</span>
              <div style={{ fontSize: '1.6rem', fontWeight: 500, color: 'var(--accent-strong)' }}>
                {formatRupiah(totalDebt.toString())}
              </div>
            </div>
            <span className="pill">{debts.reduce((n, d) => n + d.bagCount, 0)} kantong belum dibayar</span>
          </div>
          {!isHospital && (
            <table style={{ marginTop: '0.8rem' }}>
              <thead>
                <tr><th>Rumah sakit</th><th style={{ textAlign: 'right' }}>Pesanan</th>
                  <th style={{ textAlign: 'right' }}>Kantong</th><th style={{ textAlign: 'right' }}>Nilai</th></tr>
              </thead>
              <tbody>
                {debts.map((d) => (
                  <tr key={d.hospitalId}>
                    <td>{d.hospitalName}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{d.orderCount}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{d.bagCount}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>
                      {formatRupiah(d.outstandingAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="muted" style={{ fontSize: 12.5, margin: '0.6rem 0 0' }}>
            Angka ini turunan dari kantong yang benar-benar dialokasikan — membatalkan
            pesanan langsung menghapus hutangnya. Pembayaran masuk di Fase 5.
          </p>
        </div>
      )}

      {can('order:create') && (
        <form onSubmit={submitOrder} className="card stack">
          <h3>Pesan darah</h3>
          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="o-cat">Kategori pasien</label>
              <select id="o-cat" value={form.patientCategory}
                onChange={(e) => setForm({ ...form, patientCategory: e.target.value as PatientCategory })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{PATIENT_CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="o-ref">No. rekam medis</label>
              <input id="o-ref" value={form.patientReference}
                onChange={(e) => setForm({ ...form, patientReference: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="o-comp">Komponen</label>
              <select id="o-comp" value={form.componentTypeId}
                onChange={(e) => setForm({ ...form, componentTypeId: e.target.value })}>
                {components.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="o-qty">Jumlah kantong</label>
              <input id="o-qty" value={form.quantity} inputMode="numeric" required
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="o-abo">Golongan darah</label>
              <select id="o-abo" value={form.abo}
                onChange={(e) => setForm({ ...form, abo: e.target.value as BloodGroup })}>
                {BLOOD_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="o-rh">Rhesus</label>
              <select id="o-rh" value={form.rh}
                onChange={(e) => setForm({ ...form, rh: e.target.value as RhFactor })}>
                {RH_FACTORS.map((f) => (
                  <option key={f} value={f}>{f === 'positive' ? 'Positif' : 'Negatif'}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="label" htmlFor="o-letter">Surat permohonan resmi</label>
              <FileUpload
                id="o-letter"
                target="request-letter"
                value={form.requestLetterUrl}
                onChange={(url) => setForm({ ...form, requestLetterUrl: url })}
                hint="Wajib sebelum PMI bisa menyetujui pesanan."
              />
            </div>
          </div>
          <div><button type="submit" className="primary">Kirim pesanan</button></div>
        </form>
      )}

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.7rem' }}>
          <h3>Daftar pesanan</h3>
          <div className="field" style={{ minWidth: 200 }}>
            <select value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as '' | OrderStatus)}>
              <option value="">Semua status</option>
              {(Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((s) => (
                <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
        </div>
        {loading ? <p className="muted">Memuat…</p> : rows.length === 0 ? (
          <p className="muted">Belum ada pesanan.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nomor</th>{!isHospital && <th>Rumah sakit</th>}
                <th>Dikirim</th><th style={{ textAlign: 'right' }}>Kantong</th>
                <th>Status</th><th style={{ textAlign: 'right' }}>Nilai</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 12.5 }}>{r.orderNumber}</td>
                  {!isHospital && <td>{r.hospitalName}</td>}
                  <td className="muted" style={{ fontSize: 13 }}>{fmtDate(r.submittedAt)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {r.allocatedBags}/{r.requestedBags}
                    {r.allocatedBags < r.requestedBags && r.status === 'allocated' && (
                      <span className="pill accent" style={{ marginLeft: 5 }}>kurang</span>
                    )}
                  </td>
                  <td>
                    <span className={isAlert(r.status) ? 'pill accent' : 'pill'}>
                      {ORDER_STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {r.payableTotal === '0' ? '—' : formatRupiah(r.payableTotal)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button style={{ padding: '0.25rem 0.7rem', fontSize: 13 }}
                      onClick={() => void open(r.id)}>Buka</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div>
              <h3>{selected.orderNumber}</h3>
              <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: 13 }}>
                {selected.hospitalName} · {PATIENT_CATEGORY_LABELS[selected.patientCategory as PatientCategory]}
                {selected.patientReference ? ` · ${selected.patientReference}` : ''}
              </p>
            </div>
            <button style={{ padding: '0.25rem 0.7rem', fontSize: 13 }}
              onClick={() => setSelected(null)}>Tutup</button>
          </div>

          {selected.reviewNote && (
            <div className="msg error">
              <strong>Catatan PMI.</strong> {selected.reviewNote}
            </div>
          )}

          <div>
            <span className="label">Yang dipesan</span>
            <table style={{ marginTop: '0.4rem' }}>
              <thead>
                <tr><th>Komponen</th><th>Golongan</th><th style={{ textAlign: 'right' }}>Diminta</th>
                  <th style={{ textAlign: 'right' }}>Terisi</th><th style={{ textAlign: 'right' }}>Harga/kantong</th></tr>
              </thead>
              <tbody>
                {selected.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="mono">{l.componentCode}</td>
                    <td className="mono">{l.abo}{l.rh === 'positive' ? '+' : '−'}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{l.quantity}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 500 }}>{l.allocatedCount}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{formatRupiah(l.payableAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selected.allocations.length > 0 && (
            <div>
              <span className="label">Kantong yang dialokasikan</span>
              <table style={{ marginTop: '0.4rem' }}>
                <thead>
                  <tr><th>Nomor kantong</th><th>Golongan</th><th>Cabang</th>
                    <th>Kadaluarsa</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {selected.allocations.map((a) => (
                    <tr key={a.id}>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {a.unitNumber}
                        {a.isSubstitution && (
                          <span className="pill accent" style={{ marginLeft: 5 }}
                            title={a.substitutionReason ?? undefined}>substitusi</span>
                        )}
                      </td>
                      <td className="mono">{a.abo}{a.rh === 'positive' ? '+' : '−'}</td>
                      <td className="mono muted">{a.branchCode}</td>
                      <td style={{ fontSize: 12.5 }}>{fmtDate(a.effectiveExpiresAt)}</td>
                      <td>
                        <span className={a.status === 'released' ? 'pill' : 'pill accent'}
                          title={a.releaseReason ?? undefined}>
                          {ALLOCATION_STATUS_LABELS[a.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="row">
            {can('order:review') && selected.status === 'submitted' && (
              <>
                <button className="primary"
                  onClick={() => void act(
                    () => api.post(`/orders/${selected.id}/review`, { decision: 'approved', note: null }),
                    () => setNotice('Pesanan disetujui. Lanjutkan ke alokasi kantong.'),
                  )}>Setujui</button>
                <button onClick={() => {
                  const note = prompt('Perubahan apa yang diminta?');
                  if (note) void act(() => api.post(`/orders/${selected.id}/review`,
                    { decision: 'revision_requested', note }));
                }}>Minta revisi</button>
                <button className="danger" onClick={() => {
                  const note = prompt('Alasan penolakan:');
                  if (note) void act(() => api.post(`/orders/${selected.id}/review`,
                    { decision: 'rejected', note }));
                }}>Tolak</button>
              </>
            )}

            {can('order:allocate') && (selected.status === 'approved' || selected.shortfall > 0) && (
              <>
                <button className="primary" onClick={() => void allocate(selected.id, false)}>
                  Alokasikan {selected.shortfall > 0 ? `(kurang ${selected.shortfall})` : ''}
                </button>
                <button onClick={() => void allocate(selected.id, true)}>
                  Alokasikan dengan substitusi
                </button>
              </>
            )}

            {can('order:allocate') && selected.status === 'allocated' && (
              <>
                <button className="primary"
                  onClick={() => void act(
                    () => api.post(`/orders/${selected.id}/issue`, { note: null }),
                    () => setNotice('Kantong dikirim dan keluar dari stok.'),
                  )}>Kirim ke RS</button>
                <button onClick={() => {
                  const reason = prompt('Alasan melepas alokasi:');
                  if (reason) void act(() => api.post(`/orders/${selected.id}/release-allocation`, { reason }));
                }}>Lepas alokasi</button>
              </>
            )}

            {canTransitionOrder(selected.status, 'completed') && (
              <button className="primary"
                onClick={() => void act(
                  () => api.post(`/orders/${selected.id}/complete`, { note: null }),
                  () => setNotice('Penerimaan dikonfirmasi.'),
                )}>Konfirmasi diterima</button>
            )}

            {HOSPITAL_CANCELLABLE_STATUSES.includes(selected.status) && (
              <button className="danger" onClick={() => {
                const reason = prompt('Alasan pembatalan:');
                if (reason) void act(() => api.post(`/orders/${selected.id}/cancel`, { reason }));
              }}>Batalkan</button>
            )}
          </div>

          <div>
            <span className="label">Riwayat</span>
            <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.35rem' }}>
              {selected.timeline.map((t, i) => (
                <div key={i} className="row" style={{ gap: '0.6rem', fontSize: 13 }}>
                  <span className="mono muted" style={{ fontSize: 12, minWidth: 105 }}>
                    {fmtDateTime(t.createdAt)}
                  </span>
                  <span className="pill">{ORDER_STATUS_LABELS[t.toStatus as OrderStatus]}</span>
                  <span className="muted">{t.actorName}</span>
                  {t.note && <span>{t.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
